import * as React from 'react';
import styled, { css } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { getDateTimeFromDateParts, isDateInRange as isDateInRangeFromUtils, IDateParts, getDateParts, getToday, IDate, getDayName, NameLength } from '@agilepoint/utils-date-time';
import { Day, IDateInfo, SelectionMode, CalendarView, ICalendarOnRenderItem } from '../calendar-types';
import { prepareCalendarData } from '../utils';
import { CalendarDate } from './calendar-date';
import { Typography } from '../../typography';
import { selectThemeSemantics } from '../../../styling';
import { Separator } from '../../separator';

interface IDateCellBaseProps {
    dateInRange: boolean;
    isSelected: boolean;
    isInCurentMonth: boolean;
    isStartDate: boolean;
    isEndDate: boolean;
}

const DateGridTable = styled.table.attrs({
    className: String('ap-calender-dateGrid')
})`
    border-spacing: 0px 1px;
`;

const DayHeaderCell = styled.th.attrs({
    className: 'ap-calender-dayHeaderCell'
})`
    padding: 0 3px;
    height: 36px;
`;

const DateCell = styled.td.attrs({
    className: 'ap-calender-dateCell'
}) <IDateCellBaseProps>`
    text-align: center;
    width: 35px;
    height: 35px;
    padding: ${selectThemeSemantics(({ gap }) => gap.s)};
    ${({ dateInRange }) => (dateInRange) && dateRangeStyle}
`;

const GridContainer = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: auto auto auto;
    padding: 0 8px;
`;

const dateRangeStyle = css<IDateCellBaseProps>`
    background-color: ${({ isInCurentMonth, theme: { calendar } }) => isInCurentMonth && calendar.colors.dateRange.backgroundColor};
    cursor: default;
    border-top-left-radius: ${({ isStartDate }) => (isStartDate ? '50%' : 'unset')};
    border-bottom-left-radius: ${({ isStartDate }) => (isStartDate ? '50%' : 'unset')};
    border-top-right-radius: ${({ isEndDate }) => (isEndDate ? '50%' : 'unset')};
    border-bottom-right-radius: ${({ isEndDate }) => (isEndDate ? '50%' : 'unset')};
`;

function isToday(info: IDateInfo) {
    const { date, month, year } = getDateParts(getToday());
    return info.date === date && info.month === month && info.year === year;
}

const WeekNumberLabel = styled(Typography.Medium)`
    display: inline-block;
    height: 30px;
    width: 30px;
    line-height: 30px;
    text-align: center;
    box-sizing: border-box;
    user-select: none;
    color: ${({ theme: { calendar } }) => calendar.colors.disabled.foregroundColor};
`;

const StyledSeparator = styled(Separator).attrs(({ theme: { calendar: { colors } } }) => ({
    color: colors.disabled.backgroundColor
}))`
    align-self: center;
    height: calc(100% - 23px);
`;

const WeekNumberHeader = styled(DayHeaderCell).attrs({
    className: String('ap-calender-weekNumberHeader')
})`
    color: ${({ theme: { calendar } }) => calendar.colors.disabled.foregroundColor};
`;

function renderWeekNumber(weekInfo: IDateInfo[]) {
    const dateTime = getDateTimeFromDateParts(weekInfo.at(1) as IDateInfo);
    return (
        <tr className="ap-calender-weekNumber" key={dateTime.toJSON()}>
            <td>
                <WeekNumberLabel>
                    {dateTime.weekNumber}
                </WeekNumberLabel>
            </td>
        </tr>
    );
}

function WeekNumber(props: { data: IDateInfo[][] }) {
    const { data } = props;
    return (
        <DateGridTable>
            <thead>
                <tr>
                    <WeekNumberHeader>
                        <Typography.Small>
                            Wk
                        </Typography.Small>
                    </WeekNumberHeader>
                </tr>
            </thead>
            <tbody>
                {data.map(renderWeekNumber)}
            </tbody>
        </DateGridTable>
    );
}

const renderDayHeaders = memoizeFunction((weekStartDay: Day, weekDayNameLength: NameLength, culture?: string) => {
    const days: React.ReactNode[] = [];
    while (days.length < 7) {
        days.push(
            <DayHeaderCell key={weekStartDay}>
                <Typography.Small>
                    {getDayName(weekStartDay, weekDayNameLength, culture)}
                </Typography.Small>
            </DayHeaderCell>
        );
        weekStartDay = (weekStartDay + 1) % 8 || 1;
    }
    return days;
});

interface IDateGridProps extends ICalendarOnRenderItem {
    culture?: string;
    hideDateFromOtherMonth?: boolean;
    weekStartDay: Day;
    showWeekNumber?: boolean;
    view: CalendarView;
    value?: IDateParts[];
    selectionMode?: SelectionMode;
    weekDayNameLength?: NameLength;
    isDateInRange(date: IDate): boolean;
    onDateSelect: (date: IDateInfo) => void;
    onDateUnselect: (date: IDateInfo) => void;
    onDateCellMouseOver: (date: IDateInfo) => void;
}

export class DateGrid extends React.PureComponent<IDateGridProps> {

    render() {
        const {
            weekStartDay = Day.sun, weekDayNameLength = 'short', culture, view: { month, year }
        } = this.props;
        const data = prepareCalendarData(month, year, weekStartDay);

        return (
            <GridContainer>
                {this.renderWeekNumber(data)}
                <DateGridTable>
                    <thead>
                        <tr>
                            {renderDayHeaders(weekStartDay, weekDayNameLength, culture)}
                        </tr>
                    </thead>
                    <tbody>
                        {this.renderDates(data)}
                    </tbody>
                </DateGridTable>
            </GridContainer>
        );
    }

    private renderWeekNumber(data: IDateInfo[][]) {
        const { showWeekNumber } = this.props;
        return showWeekNumber ? (
            <>
                <WeekNumber data={data} />
                <StyledSeparator orientation="vertical" hideBeak />
            </>
        ) : null;
    }

    private renderDates(data: IDateInfo[][]) {
        const { view: { month } } = this.props;
        return data.map((rowInfo, idx) => (
            <tr key={`${month}_${idx}`}>
                {rowInfo.map(this.renderDateColumn)}
            </tr>
        ));
    }

    private renderDateColumn = (info: IDateInfo) => {
        const { onDateSelect, onDateUnselect, value, selectionMode, onDateCellMouseOver, isDateInRange, onRenderItem, hideDateFromOtherMonth } = this.props;
        const { isInCurentMonth } = info;
        const isSelected = this.isDateSelected(info);
        const isDisabled = !isDateInRange(getDateTimeFromDateParts(info));
        const isItemInRange = (selectionMode === 'range' && value!.length === 2 && !isDisabled) ? this.isDatePartsInRange(info, value!) : false;
        const [startDateParts, endDateParts] = value!;
        const isStartDate = (selectionMode === 'range' && value!.length === 2) ? this.hasSameDate(info, startDateParts) : false;
        const isEndDate = (selectionMode === 'range' && value!.length === 2) ? this.hasSameDate(info, endDateParts) : false;
        const hideDate = hideDateFromOtherMonth && !isInCurentMonth;

        return (
            <DateCell
                key={`${info.date}_${info.month}`}
                isSelected={isSelected}
                isInCurentMonth={isInCurentMonth}
                dateInRange={isItemInRange}
                isStartDate={isStartDate}
                isEndDate={isEndDate}>
                {
                    hideDate
                        ? null
                        : <CalendarDate
                            isSelected={isSelected}
                            isToday={!isSelected && isToday(info)}
                            isInCurentMonth={isInCurentMonth}
                            info={info}
                            disabled={isDisabled}
                            selectionMode={selectionMode}
                            onSelect={onDateSelect}
                            onUnselect={onDateUnselect}
                            onDateCellMouseOver={onDateCellMouseOver}
                            dateInRange={isItemInRange}
                            onRenderItem={onRenderItem} />
                }
            </DateCell>
        );
    }

    private isDatePartsInRange = (info: IDateInfo, value: IDateParts[]) => {
        const [startDateParts, endDateParts] = value;
        const startDate = getDateTimeFromDateParts(startDateParts);
        const endDate = getDateTimeFromDateParts(endDateParts);
        return isDateInRangeFromUtils(getDateTimeFromDateParts(info), startDate, endDate);
    }

    private hasSameDate = (info: IDateInfo, value: IDateParts) => {
        const { date, month, year } = value;
        return (info.year === year && info.month === month && info.date === date);
    }

    private isDateSelected(info: IDateInfo) {
        const { value } = this.props;
        if (value === undefined) {
            return false;
        }
        return value.some((dateParts) => this.hasSameDate(info, dateParts));
    }
}
